import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    alpha,
    useTheme,
    Tabs,
    Tab,
    IconButton,
    Chip,
    Avatar,
    Tooltip,
    Skeleton,
    Grid,
    Paper,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    SmartToy as AIPageIcon,
    Chat as ChatIcon,
    Psychology as AgentsIcon,
    Timeline as AnalyticsIcon,
    AutoAwesome as GenerativeIcon,
    Send as SendIcon,
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    MoreVert as MoreIcon,
    Warning as WarningIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    BugReport as AnomalyIcon,
    Lightbulb as InsightIcon,
    Settings as SettingsIcon,
    PlayArrow as ConnectIcon,
    Stop as DisconnectIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import PageWrapper from '../../components/common/PageWrapper';
import StatsCard, { StatsCardGrid } from '../../components/common/StatsCard';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import GlassPanel from '../../components/common/GlassPanel';
import {
    useAIChatMessages,
    useAIAnomalies,
    useAIPredictions,
    useSendAIChat,
    useAICapabilities,
    useAIAgents
} from '../../hooks/useAI';

/**
 * AICockpitPage - High-fidelity AI & Autonomous Systems Center
 * Aurora 2026 Design Standard
 */
const AICockpitPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const chatEndRef = useRef(null);

    // Tab Management
    const queryParams = new URLSearchParams(location.search);
    const initialTab = parseInt(queryParams.get('tab') || '0', 10);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [chatInput, setChatInput] = useState('');

    // Data Fetching
    const { data: chatMessages, isLoading: chatLoading } = useAIChatMessages();
    const { data: anomalies, isLoading: anomaliesLoading } = useAIAnomalies();
    const { data: predictions, isLoading: predictionsLoading } = useAIPredictions();
    const { data: capabilities, isLoading: capabilitiesLoading } = useAICapabilities();
    const { data: agentsData, isLoading: agentsLoading } = useAIAgents();
    const sendChat = useSendAIChat();
    
    // Process agents data from API
    const agents = Array.isArray(agentsData) ? agentsData : [];

    // State for optimistic chat
    const [localMessages, setLocalMessages] = useState([]);

    useEffect(() => {
        if (chatMessages && Array.isArray(chatMessages)) {
            setLocalMessages(chatMessages);
        }
    }, [chatMessages]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [localMessages, activeTab]);

    // Handlers
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSelectedAgent(null);
        setIsSidebarOpen(false);
        navigate(`/admin/ai?tab=${newValue}`, { replace: true });
    };

    const handleSendMessage = async () => {
        if (!chatInput.trim()) return;

        const userMsg = {
            id: Date.now(),
            role: 'user',
            content: chatInput,
            timestamp: new Date().toLocaleTimeString()
        };

        setLocalMessages(prev => [...prev, userMsg]);
        const currentInput = chatInput;
        setChatInput('');

        try {
            const response = await sendChat.mutateAsync({ content: currentInput });
            setLocalMessages(prev => [...prev, response]);
        } catch (err) {
            setLocalMessages(prev => [...prev, {
                id: Date.now() + 1,
                role: 'system',
                content: t('ai.chat.error', 'Error connecting to neural core.'),
                isError: true
            }]);
        }
    };

    // Agent DataGrid column definitions (data comes from useAIAgents hook)
    const agentColumns = [
        { field: 'id', headerName: 'ID', width: 90 },
        {
            field: 'name',
            headerName: t('ai.agent.name', 'Agent Name'),
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: params.row.color || theme.palette.primary.main }}>
                        {params.row.name?.[0]}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">{params.value}</Typography>
                </Box>
            )
        },
        {
            field: 'status',
            headerName: t('common.status', 'Status'),
            width: 120,
            renderCell: (params) => {
                const color = params.value === 'active' ? 'success' : params.value === 'idle' ? 'warning' : 'default';
                return <Chip label={params.value} size="small" color={color} variant="outlined" />;
            }
        },
        {
            field: 'tasks_completed',
            headerName: t('ai.agent.tasks', 'Tasks 24h'),
            width: 130,
            renderCell: (params) => (
                <Typography variant="body2">{params.value || 0}</Typography>
            )
        },
        {
            field: 'accuracy',
            headerName: t('ai.agent.accuracy', 'Accuracy'),
            width: 120,
            renderCell: (params) => (
                <Typography variant="body2" color="success.main">{params.value}%</Typography>
            )
        }
    ];

    const getRiskColor = (score) => {
        if (score >= 0.8) return 'error';
        if (score >= 0.6) return 'warning';
        return 'info';
    };

    return (
        <PageWrapper
            title={t('ai.cockpit.title', 'AI & Autonomous Systems')}
            subtitle={t('ai.cockpit.subtitle', 'Neural orchestration, predictive analytics, and autonomous agent fleet management')}
            breadcrumbs={[
                { label: t('nav.admin', 'Admin'), path: '/admin' },
                { label: t('ai.cockpit.title', 'AI Cockpit'), icon: AIPageIcon }
            ]}
            actions={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => window.location.reload()}
                    >
                        {t('common.refresh', 'Refresh System')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<GenerativeIcon />}
                        color="secondary"
                    >
                        {t('ai.studio.launch', 'Launch Studio')}
                    </Button>
                </Box>
            }
        >
            {/* KPI Cards Section */}
            <Box sx={{ mb: 4 }}>
                <StatsCardGrid columns={4}>
                    <StatsCard
                        icon={AgentsIcon}
                        title={t('ai.kpi.activeAgents', 'Active Neural Agents')}
                        value={chatLoading ? <Skeleton width={40} /> : '8/12'}
                        subtitle={t('ai.kpi.fleetCapacity', 'Fleet Capacity: 66%')}
                        color={theme.palette.success.main}
                    />
                    <StatsCard
                        icon={AnomalyIcon}
                        title={t('ai.kpi.anomalies', 'Detected Anomalies')}
                        value={anomaliesLoading ? <Skeleton width={40} /> : anomalies?.length || 0}
                        trend="+2 New"
                        trendColor="warning"
                        color={theme.palette.warning.main}
                    />
                    <StatsCard
                        icon={InsightIcon}
                        title={t('ai.kpi.predictions', 'Prediction Confidence')}
                        value={predictionsLoading ? <Skeleton width={50} /> : `${predictions?.accuracy ?? predictions?.confidence ?? '—'}%`}
                        subtitle={t('ai.kpi.globalModelAccuracy', 'Global Model Accuracy')}
                        color={theme.palette.info.main}
                    />
                    <StatsCard
                        icon={GenerativeIcon}
                        title={t('ai.kpi.tokens', 'Generative Tokens')}
                        value={capabilitiesLoading ? <Skeleton width={80} /> : '1.2M'}
                        subtitle={t('ai.kpi.usageCycle', 'Usage Cycle: Jan 2026')}
                        color={theme.palette.secondary.main}
                    />
                </StatsCardGrid>
            </Box>

            {/* Main Cockpit Interface */}
            <GlassPanel sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 400px)', minHeight: 650 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 64 }}>
                        <Tab
                            icon={<ChatIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('ai.tabs.assistant', 'Assistant & Chat')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<AgentsIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('ai.tabs.agents', 'Autonomous Agents')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<AnalyticsIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('ai.tabs.analytics', 'Predictive Analytics')}
                            sx={{ minHeight: 64 }}
                        />
                        <Tab
                            icon={<GenerativeIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label={t('ai.tabs.generative', 'Generative Studio')}
                            sx={{ minHeight: 64 }}
                        />
                    </Tabs>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('ai.neuralConfig', 'Neural Config')}>
                            <IconButton size="small"><SettingsIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        {/* Main Content Area */}
                        <Box sx={{
                            flexGrow: 1,
                            height: '100%',
                            width: isSidebarOpen ? 'calc(100% - 400px)' : '100%',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>

                            {/* Tab 0: Assistant & Chat */}
                            {activeTab === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {localMessages.map((msg, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                                <Paper sx={{
                                                    p: 2,
                                                    maxWidth: '70%',
                                                    bgcolor: msg.role === 'user' ? 'primary.main' : alpha(theme.palette.background.paper, 0.8),
                                                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                                    borderRadius: 3,
                                                    borderTopRightRadius: msg.role === 'user' ? 0 : 3,
                                                    borderTopLeftRadius: msg.role === 'user' ? 3 : 0,
                                                    boxShadow: 3
                                                }}>
                                                    <Typography variant="body1">{msg.content}</Typography>
                                                    <Typography variant="caption" sx={{ opacity: 0.7, mt: 1, display: 'block', textAlign: 'right' }}>
                                                        {msg.timestamp || new Date().toLocaleTimeString()}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        ))}
                                        {sendChat.isPending && (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1 }}>
                                                <Skeleton variant="circular" width={10} height={10} />
                                                <Skeleton variant="circular" width={10} height={10} />
                                                <Skeleton variant="circular" width={10} height={10} />
                                            </Box>
                                        )}
                                        <div ref={chatEndRef} />
                                    </Box>
                                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField
                                                fullWidth
                                                placeholder={t('ai.chat.placeholder', 'Ask Aurora AI something...')}
                                                variant="outlined"
                                                size="small"
                                                value={chatInput}
                                                onChange={(e) => setChatInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={handleSendMessage}
                                                disabled={!chatInput.trim() || sendChat.isPending}
                                            >
                                                <SendIcon />
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {/* Tab 1: Autonomous Agents */}
                            {activeTab === 1 && (
                                <Box sx={{ height: '100%', width: '100%' }}>
                                    <SmartDataGrid
                                        rows={agents}
                                        columns={agentColumns}
                                        onRowClick={(params) => {
                                            setSelectedAgent(params.row);
                                            setIsSidebarOpen(true);
                                        }}
                                    />
                                </Box>
                            )}

                            {/* Tab 2: Predictive Analytics */}
                            {activeTab === 2 && (
                                <Box sx={{ p: 3, overflowY: 'auto' }}>
                                    <Typography variant="h6" gutterBottom>{t('ai.analytics.anomalyDetection', 'Anomaly Detection Stream')}</Typography>
                                    <Grid container spacing={2}>
                                        {anomalies?.map((anomaly, idx) => (
                                            <Grid item xs={12} md={6} key={idx}>
                                                <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                                    <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                                                        <WarningIcon />
                                                    </Avatar>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                            <Typography variant="subtitle1" fontWeight="bold">{anomaly.type || '-'}</Typography>
                                                            <Chip size="small" label={`${Math.round((anomaly.risk_score || 0) * 100)}% Risk`} color={getRiskColor(anomaly.risk_score || 0)} />
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">{anomaly.description}</Typography>
                                                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', p: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                                ID: {anomaly.order_id || 'SYS-UNK'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        ))}
                                        {!anomalies?.length && !anomaliesLoading && (
                                            <Box sx={{ width: '100%', p: 4, textAlign: 'center' }}>
                                                <SuccessIcon sx={{ fontSize: 60, color: 'success.light', mb: 2 }} />
                                                <Typography color="text.secondary">{t('ai.analytics.noAnomalies', 'System Nominal. No anomalies detected.')}</Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                </Box>
                            )}

                            {/* Tab 3: Generative Studio */}
                            {activeTab === 3 && (
                                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <GenerativeIcon sx={{ fontSize: 80, color: 'divider', mb: 2 }} />
                                                                        <Typography variant="h5" color="text.secondary">{t('ai.studio.title', 'Aurora Generative Studio')}</Typography>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 500, textAlign: 'center' }}>
                                                                            {t('ai.studio.description', 'Generate marketing assets, optimize warehouse layouts, and simulate scenario modeling using our proprietary LLM cluster.')}
                                                                        </Typography>
                                                                        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                                                                            <Button variant="outlined">{t('ai.studio.imageGen', 'Image Gen')}</Button>
                                                                            <Button variant="outlined">{t('ai.studio.copywriting', 'Copywriting')}</Button>
                                                                            <Button variant="outlined">{t('ai.studio.scenarioSim', 'Scenario Sim')}</Button>
                                                                        </Box>
                                </Box>
                            )}

                        </Box>

                        {/* Glass Sidebar for Agent Details */}
                        {isSidebarOpen && selectedAgent && activeTab === 1 && (
                            <Box sx={{
                                width: 400,
                                borderLeft: '1px solid',
                                borderColor: 'divider',
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(20px)',
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                animation: 'slideIn 0.3s ease-out'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                    <Typography variant="h6">{t('ai.agent.protocol', 'Agent Protocol')}</Typography>
                                    <IconButton onClick={() => setIsSidebarOpen(false)} size="small">
                                        <MoreIcon sx={{ transform: 'rotate(90deg)' }} />
                                    </IconButton>
                                </Box>

                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <Avatar sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: selectedAgent.color, mx: 'auto', mb: 2 }}>
                                        {selectedAgent.name[0]}
                                    </Avatar>
                                    <Typography variant="h5">{selectedAgent.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">ID: {selectedAgent.id}</Typography>
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                                        <Chip label={selectedAgent.status} color={selectedAgent.status === 'active' ? 'success' : 'default'} />
                                        <Chip label="v2.4.0" variant="outlined" />
                                    </Box>
                                </Box>

                                <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, mb: 3 }}>
                                    <ListItem>
                                        <ListItemIcon><SuccessIcon color="success" /></ListItemIcon>
                                        <ListItemText primary={t('ai.agent.uptime', 'Uptime')} secondary={t('ai.agent.uptimeValue', '99.98% (Last 30 days)')} />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><ConnectIcon color="primary" /></ListItemIcon>
                                        <ListItemText primary={t('ai.agent.throughput', 'Throughput')} secondary={`${selectedAgent.tasks_completed} ${t('ai.agent.opsPerHour', 'Ops/hr')}`} />
                                    </ListItem>
                                </List>

                                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                        <Button variant="contained" startIcon={<SettingsIcon />}>{t('ai.agent.configureParameters', 'Configure Parameters')}</Button>
                                                                        <Button variant="outlined" color="error" startIcon={<DisconnectIcon />}>{t('ai.agent.emergencyStop', 'Emergency Stop')}</Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </GlassPanel>

            <style>
                {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
            </style>
        </PageWrapper>
    );
};

export default React.memo(AICockpitPage);